import React, { useState } from "react";

import CalendarIcon from "@uui/Calendar";
import { compact, map } from "lodash/fp";

import ButtonGroup from "@mui/material/ButtonGroup";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";

import useDateRangeStore from "@app/analytics/stores/useDateRangeStore";
import type { CustomDateRange, DateRangePreset } from "@app/analytics/types";
import useAnchorElement from "@app/utils/hooks/useAnchorElement";

import DropdownMenuItem from "@rui/Dropdown/DropdownMenuItem";
import { Column, Row } from "@rui/Stack";
import { Span } from "@rui/styles";
import Switch from "@rui/Switch";

import CustomDateRangePicker, {
  CustomDateRangePickerProps,
} from "./CustomDateRangePicker";
import { CompareWrapper, Menu, MenuButton } from "./styles";
import {
  RANGE_PRESETS,
  getDateLabels,
  getStartAndEndDatesFromRangePreset,
} from "./utils";

// exclude the 60-day preset by default
const DEFAULT_INCLUDED_PRESETS: DateRangePreset[] = [
  "0",
  "7",
  "30",
  "90",
  "180",
  "365",
];

interface ConnectedDateSelectorProps {
  allowCompare?: boolean;
  showCustomDateRange?: boolean;
  fixedRange?: DateRangePreset;
  includePresets?: DateRangePreset[];
}

interface DateSelectorProps extends ConnectedDateSelectorProps {
  comparing: boolean;
  customDateRange: CustomDateRange | null;
  dateRangePreset: DateRangePreset | null;
  onSelectCustomDateRange: CustomDateRangePickerProps["onChange"];
  onSelectRangePreset: (rangePreset: DateRangePreset) => void;
  onToggleCompare: (comparing: boolean) => void;
}

const getSelectedRange = ({
  customDateRange,
  dateRangePreset,
}: {
  customDateRange: CustomDateRange | null;
  dateRangePreset: DateRangePreset | null;
}): CustomDateRange | undefined => {
  if (customDateRange) return customDateRange;
  if (dateRangePreset) {
    const { startDate, endDate } =
      getStartAndEndDatesFromRangePreset(dateRangePreset);
    return [startDate, endDate];
  }
};

const DateSelector = ({
  allowCompare = false,
  showCustomDateRange = true,
  comparing,
  customDateRange,
  dateRangePreset,
  fixedRange,
  includePresets = DEFAULT_INCLUDED_PRESETS,
  onSelectCustomDateRange,
  onSelectRangePreset,
  onToggleCompare,
}: DateSelectorProps) => {
  const [selectingCustomDateRange, setSelectingCustomDateRange] =
    useState(false);

  const {
    anchorElement: rangeMenuAnchorEl,
    open: rangeMenuOpen,
    onClose: onRangeMenuClose,
    onClick: onRangeMenuClick,
  } = useAnchorElement();

  const handleCloseRangeMenu = () => {
    setSelectingCustomDateRange(false);
    onRangeMenuClose();
  };

  const handleCompareChange = () => {
    if (fixedRange != null) return;
    onToggleCompare(!comparing);
    onRangeMenuClose();
  };

  const handleSelectRangePreset = (rangePreset: DateRangePreset) => {
    if (fixedRange != null) return;
    handleCloseRangeMenu();
    onSelectRangePreset(rangePreset);
  };

  const handleClickCustomDateRange = () => {
    onRangeMenuClose();
    setSelectingCustomDateRange(true);

    if (dateRangePreset) {
      const { startDate, endDate } =
        getStartAndEndDatesFromRangePreset(dateRangePreset);
      onSelectCustomDateRange([startDate, endDate]);
    }
  };

  const handleSubmitCustomDateRange: CustomDateRangePickerProps["onChange"] = (
    range
  ) => {
    if (fixedRange != null) return;
    onSelectCustomDateRange(range);
    handleCloseRangeMenu();
  };

  const rangeValue = fixedRange ?? dateRangePreset;
  const canChangeDates = fixedRange == null;
  const isCustomDateRangeSelected = dateRangePreset == null;
  const shouldAllowCompare = canChangeDates && allowCompare;

  const { dateLabel, compareDateLabel } = getDateLabels({
    comparing: shouldAllowCompare && comparing,
    rangePreset: rangeValue,
    customDateRange: fixedRange ? null : customDateRange,
  });

  const presetOptions = compact(
    map(
      ([value, label]) =>
        includePresets?.includes(value as DateRangePreset)
          ? { value: value as DateRangePreset, label }
          : null,
      Object.entries(RANGE_PRESETS)
    )
  );

  return (
    <Row position="relative" width="fit-content">
      <ButtonGroup>
        <MenuButton
          disabled={!canChangeDates}
          onClick={onRangeMenuClick}
          aria-haspopup
          aria-expanded={rangeMenuOpen ? "true" : "false"}
        >
          {rangeValue ? RANGE_PRESETS[rangeValue] : "Custom"}
        </MenuButton>

        {showCustomDateRange && (
          <MenuButton
            disabled={!canChangeDates}
            startIcon={<CalendarIcon />}
            onClick={() => setSelectingCustomDateRange(true)}
            sx={{ position: "relative" }}
            aria-haspopup
            aria-expanded={selectingCustomDateRange ? "true" : "false"}
          >
            {dateLabel}
            {compareDateLabel && (
              <Span sx={{ color: "shades.neutral.400" }}>
                &nbsp;({compareDateLabel})
              </Span>
            )}
          </MenuButton>
        )}
      </ButtonGroup>

      <Menu
        anchorEl={rangeMenuAnchorEl}
        open={rangeMenuOpen}
        onClose={handleCloseRangeMenu}
        sx={{ "& .MuiPaper-root": { width: "220px" } }}
      >
        {presetOptions.map(({ value, label }) => (
          <DropdownMenuItem
            key={value}
            selected={value === dateRangePreset}
            onClick={() => handleSelectRangePreset(value)}
            label={label}
          />
        ))}
        <Divider sx={{ borderColor: "shades.neutral.100", mx: 1 }} />
        <DropdownMenuItem
          selected={isCustomDateRangeSelected}
          label="Custom date range"
          onClick={handleClickCustomDateRange}
        />
        {shouldAllowCompare && (
          <Column sx={{ p: 1, pb: 0 }}>
            <Divider sx={{ borderColor: "shades.neutral.100" }} />
            <CompareWrapper spacing={1}>
              <Typography variant="bodySecondaryReg" color="shades.neutral.500">
                Compare to previous period
              </Typography>
              <Switch
                checked={comparing}
                onClick={handleCompareChange}
                sx={{ mr: 0 }}
              />
            </CompareWrapper>
          </Column>
        )}
      </Menu>
      {selectingCustomDateRange && (
        <CustomDateRangePicker
          onChange={handleSubmitCustomDateRange}
          onClose={() => setSelectingCustomDateRange(false)}
          value={getSelectedRange({ customDateRange, dateRangePreset })}
        />
      )}
    </Row>
  );
};

const ConnectedDateSelector = (props: ConnectedDateSelectorProps) => {
  const dateRangeStore = useDateRangeStore();

  return (
    <DateSelector
      {...props}
      comparing={dateRangeStore.comparing}
      customDateRange={dateRangeStore.customDateRange}
      onSelectCustomDateRange={dateRangeStore.setCustomDateRange}
      onSelectRangePreset={dateRangeStore.setDateRangePreset}
      onToggleCompare={dateRangeStore.setComparing}
      dateRangePreset={dateRangeStore.dateRangePreset}
    />
  );
};

export { DateSelector };
export default ConnectedDateSelector;
